// Route: /transforms — admin-gated list page for transforms.
// Shows all non-deleted transforms with an editor sheet to create and edit them.

import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Shuffle, Plus } from "lucide-react";
import { useRole } from "@/hooks/useRole";
import { useTransforms } from "@/hooks/useTransforms";
import { useObjectTypes } from "@/hooks/useObjectTypes";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/feedback/EmptyState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TransformEditorSheet, TriggerModeBadge } from "@/components/ontology/TransformEditorSheet";
import type { Transform } from "@/types/ontology";

export default function TransformsPage() {
  const { isAdmin, isLoading: roleLoading } = useRole();
  const { transforms, isLoading } = useTransforms();
  const { objectTypes } = useObjectTypes();

  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTransformId, setSelectedTransformId] = useState<string | null>(null);

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Build a lookup map from object type id → name.
  const objectTypeNameById = Object.fromEntries(objectTypes.map((ot) => [ot.id, ot.name]));

  function openNew() {
    setSelectedTransformId(null);
    setEditorOpen(true);
  }

  function openEdit(transform: Transform) {
    setSelectedTransformId(transform.id);
    setEditorOpen(true);
  }

  function handleEditorClose(open: boolean) {
    setEditorOpen(open);
    if (!open) {
      setSelectedTransformId(null);
    }
  }

  return (
    <main className="w-full flex flex-col">
      <PageHeader
        title="Transformer"
        subtitle="Define and manage data transforms between object types."
        actions={
          <Button onClick={openNew} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
            New transform
          </Button>
        }
      />

      <div className="px-6 py-6 space-y-8">
        {isLoading ? (
          <TransformsTableSkeleton />
        ) : transforms.length === 0 ? (
          <EmptyState
            icon={Shuffle}
            title="No transforms yet"
            description="Create a transform to map data from one object type to another."
            action={
              <Button onClick={openNew}>
                <Plus className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                New transform
              </Button>
            }
          />
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>API name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Version</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transforms.map((transform) => (
                  <TransformRow
                    key={transform.id}
                    transform={transform}
                    objectTypeNameById={objectTypeNameById}
                    onClick={() => openEdit(transform)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <TransformEditorSheet
        open={editorOpen}
        onOpenChange={handleEditorClose}
        transformId={selectedTransformId}
        onCreated={(id) => setSelectedTransformId(id)}
      />
    </main>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface TransformRowProps {
  transform: Transform;
  objectTypeNameById: Record<string, string>;
  onClick: () => void;
}

function TransformRow({ transform, objectTypeNameById, onClick }: TransformRowProps) {
  const sourceName = objectTypeNameById[transform.source_object_type_id] ?? (
    <span className="font-mono text-xs text-muted-foreground">
      {transform.source_object_type_id.slice(0, 8)}…
    </span>
  );
  const targetName = objectTypeNameById[transform.target_object_type_id] ?? (
    <span className="font-mono text-xs text-muted-foreground">
      {transform.target_object_type_id.slice(0, 8)}…
    </span>
  );

  return (
    <TableRow
      className="hover:bg-muted/40 cursor-pointer"
      onClick={onClick}
      data-testid="transform-row"
    >
      <TableCell className="font-medium">{transform.name}</TableCell>
      <TableCell>
        <span className="font-mono text-xs text-muted-foreground">{transform.api_name}</span>
      </TableCell>
      <TableCell className="text-sm">{sourceName}</TableCell>
      <TableCell className="text-sm">{targetName}</TableCell>
      <TableCell>
        <TriggerModeBadge mode={transform.trigger_mode} />
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs font-mono">
          v{transform.current_version}
        </Badge>
      </TableCell>
    </TableRow>
  );
}

function TransformsTableSkeleton() {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Name</TableHead>
            <TableHead>API name</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Target</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead>Version</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 4 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-40" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-32" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-28" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-28" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-16" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-10" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
